<script setup>
import {computed, inject, onMounted, provide, ref} from "vue";
import TrackerNotificationSettings from "@/components/trackers/TrackerNotificationsSettings.vue";
import trackersService from "@/services/trackers.service";
import tasksService from "@/services/tasks.service";
import userService from "@/services/user.service";
import TasksList from "@/components/tasks/TasksListModal.vue";
import SideBarActions from "@/views/trackers/tracker/SideBarActions.vue";
import SideBarInfo from "@/views/trackers/tracker/SideBarInfo.vue";
import {useStore} from "vue2-helpers/vuex";
import {trackerTypes} from "@/constants/trackers";
import IntelligenceReportModal from "@/components/trackers/IntelligenceReportModal.vue";
import ShareActions from "@/components/trackers/shareActions/ShareActions.vue";
// eslint-disable-next-line
import Teleport from "vue2-teleport";
import {getNoun} from "../../../helpers/string";
import UnappliedStartupsModal from "@/views/trackers/tracker/UnappliedStartupsModal.vue";
import SideBarTrackerUpdates from "@/views/trackers/tracker/SideBarTrackerUpdates.vue";
import SideBarProjectUpdates from "@/views/trackers/tracker/SideBarProjectUpdates.vue";

const store = useStore();

const tracker = inject('tracker');
const trackerId = tracker.value.id;
const trackerInfoChanged = inject('trackerInfoChanged', null);
const companiesCount = inject('companiesCount');
const getSharedEcosystems = inject('getSharedEcosystems', null);
const showSideBar = inject('showSideBar');
const isProject = inject('isProject', false);

const notificationSettingsModal = ref(null);
const intelligenceReportModal = ref(null);
const unappliedStartupsModal = ref(null);
const tasksListModal = ref(null);
const tasksListType = ref('');
const tasksCount = ref(0);
const openTasksCount = ref(0);

const notificationsEnabled = computed(() => tracker.value.notifications.global || tracker.value.notifications.settings.trackers !== 'never' || tracker.value.notifications.settings.startups !== 'never');

onMounted(() => {
    getTasksListType();
    getTasksCount();
});

function showNotificationSettings() {
    notificationSettingsModal.value.open();
}

async function setNotificationSettings(settings) {
    await trackersService.saveNotificationsSettings(trackerId, settings);
    trackerInfoChanged();
}

async function getTasksListType() {
    const {data} = await userService.getTaskType(0, trackerId);
    tasksListType.value = data;
}

function getTasksIndexList(listType, data, pageParams) {
    return tasksService.getIndex(listType, 0, trackerId, 0, data, pageParams);
}

async function getTasksCount() {
    const {data} = await trackersService.getTasksCount(trackerId);
    [tasksCount.value, openTasksCount.value] = data;
}

provide('tasksListType', tasksListType);
provide('tasksCount', tasksCount);
provide('getTasksIndexList', getTasksIndexList);
provide('trackerId', trackerId);
</script>

<template>
    <div class="sidebar-manage" :class="showSideBar ? 'sidebar-manage--open' : 'sidebar-manage--not-open'">
        <header class="sidebar-manage__header">
            <div class="sidebar-manage__header__text">
                <SideBarActions
                    @updateTasks="getTasksCount"
                />
            </div>

            <a @click="showSideBar = !showSideBar" class="aside__collapse"></a>
        </header>

        <div class="sidebar-manage__inner">
            <SideBarInfo/>

            <template v-if="store.getters.isEnterprise && !isProject">
                <div class="sidebar-manage__plate sidebar-manage__plate--dark">
                    <ul class="list-tracker-action">
                        <li class="list-tracker-action__task" @click="tasksListModal.open()">
                            Tasks <template v-if="openTasksCount">({{openTasksCount}})</template>
                        </li>

                        <li
                            v-if="tracker.notifications"
                            class="list-tracker-action__notify"
                            :class="{'list-tracker-action__notify--enabled': notificationsEnabled}"
                            @click="showNotificationSettings"
                        >
                            Notify
                        </li>

                        <li v-if="!tracker.needs_approval" class="list-tracker-action__share list-tracker-action__share--icon">
                            <ShareActions
                                v-if="store.getters.isEnterprise"
                                :tracker-id="trackerId"
                                :tracker-type="tracker.type"
                                :is-referral="tracker.only_referral"
                                :referral-url="tracker.referral_url"
                                @accessSettingsUpdated="getSharedEcosystems"
                            />
                        </li>
                    </ul>
                </div>

                <div
                    v-if="companiesCount > 0 && tracker.type !== trackerTypes.ECOSYSTEMS
                && !store.getters.settings.disable_ai && !store.getters.isFreeEnterprise"
                    class="sidebar-manage__plate sidebar-manage__plate--dark"
                >
                    <a @click="intelligenceReportModal.open()" class="link-generate">Generate Tracker Report</a>
                </div>

                <Teleport v-if="tracker.type !== trackerTypes.ECOSYSTEMS && tracker.unapplied_startups_count" to="#unapplied-startups-block">
                    <h6 class="sidebar-manage__plate__colored__title">{{tracker.unapplied_startups_count}} Unapplied {{getNoun('Startup',tracker.unapplied_startups_count )}}</h6>
                    <a class="link" @click="unappliedStartupsModal.open()">Review</a>
                </Teleport>
            </template>

            <template v-if="store.getters.isEnterprise">
                <SideBarProjectUpdates v-if="isProject"/>
                <SideBarTrackerUpdates v-else/>
            </template>
        </div>

        <TrackerNotificationSettings
            v-if="tracker.notifications"
            ref="notificationSettingsModal"
            :tracker-type="tracker.type"
            :tracker-id="tracker.id"
            :init-settings="tracker.notifications"
            @saved="setNotificationSettings"
        />

        <TasksList
            v-if="!isProject"
            ref="tasksListModal"
            @tasksChanged="getTasksCount"
        />

        <IntelligenceReportModal
            v-if="!isProject"
            ref="intelligenceReportModal"
            :id="trackerId"
            :name="tracker.name"
        />

        <UnappliedStartupsModal
            v-if="!isProject"
            ref="unappliedStartupsModal"
        />
    </div>
</template>

<style scoped>

</style>
